import { ButtonIcon, Chip, Icon } from "@saviynt/design-system";
import { Link } from "@tanstack/react-router";
import { BrandLockup } from "../BrandLockup";
import { type Tenant } from "../TenantSwitcher";
import { UserProfile } from "../UserProfile";

export interface HeaderUser {
  displayName?: string;
  email?: string;
}

/** Trial countdown shown as a chip next to the user profile. */
export interface HeaderTrial {
  /** Pre-formatted, localized label, e.g. "30 days left". */
  label: string;
  /** Urgency bucket — drives the chip color as the trial runs down. */
  tone: "success" | "warning" | "critical";
}

interface HeaderProps {
  theme: "light" | "dark";
  onToggleTheme: () => void;
  onLogout: () => void;
  /** Optional user info for profile display (e.g. "First Last") */
  user?: HeaderUser;
  /**
   * When false, hides the Saviynt logo on the header — typically used when the
   * Sidenav is expanded/pinned and already renders the logo on itself.
   * Defaults to true.
   */
  showLogo?: boolean;
  /**
   * When false, hides Help and Notifications icons from the header.
   * Defaults to true.
   */
  showHeaderActions?: boolean;
  /** Tenant list for the switcher — rendered inside the user profile dropdown. */
  tenants?: Tenant[];
  /** The tenantId currently active. */
  currentTenantId?: string;
  /** Called when the user selects a different tenant. */
  onTenantSelect?: (tenantId: string) => void;
  /** True while a tenant switch API call is in-flight. */
  isSwitchingTenant?: boolean;
  /** When provided, renders a trial countdown chip before the profile menu. */
  trial?: HeaderTrial;
}

export function Header({
  theme,
  onToggleTheme,
  onLogout,
  user,
  showLogo = true,
  showHeaderActions = true,
  tenants,
  currentTenantId,
  onTenantSelect,
  isSwitchingTenant = false,
  trial,
}: HeaderProps) {
  return (
    <header className="h-16 border-b border-[var(--color-border-neutral-subtlest)] bg-[var(--color-background-neutral-subtlest)] flex items-center justify-between px-6">
      <div className="flex items-center gap-4">
        {showLogo && (
          <Link to="/" className="flex items-center">
            <BrandLockup variant={theme} size="md" />
          </Link>
        )}
      </div>
      <div className="flex items-center gap-2">
        {trial && (
          <Chip
            kind="boldFilled"
            size="small"
            color={trial.tone}
            label={trial.label}
          />
        )}
        {showHeaderActions && (
          <>
            <ButtonIcon
              kind="outlinedNeutral"
              icon={<Icon kind="HelpOutlined" size="small" />}
              size="medium"
              ariaLabel="Help"
              dataTestId="shell-header-help-btn"
            />
            <ButtonIcon
              kind="outlinedNeutral"
              icon={<Icon kind="Notification" size="small" />}
              size="medium"
              ariaLabel="Notifications"
              dataTestId="shell-header-notifications-btn"
            />
          </>
        )}
        <UserProfile
          onLogout={onLogout}
          userName={user?.displayName}
          userEmail={user?.email}
          theme={theme}
          onToggleTheme={onToggleTheme}
          tenants={tenants}
          currentTenantId={currentTenantId}
          onTenantSelect={onTenantSelect}
          isSwitchingTenant={isSwitchingTenant}
        />
      </div>
    </header>
  );
}
